import { createI18n } from 'vue-i18n';
import { storageLocal } from '@pureadmin/utils';
import { systemEnum, Lang } from '@/config';
import { useSystemConfigStoreHook } from '@/store/modules/systemConfig';
/* ---------- 自动加载所有 yaml/json ---------- */
// 加载src/locales下的文件
const modules = import.meta.glob('../locales/*.{yaml,yml,json}', {
  eager: true,
  import: 'default',
});

const messages = Object.fromEntries(
  Object.entries(modules).map(([path, v]) => {
    const locale = path.match(/\/([\w-]+)\.(yaml|yml|json)$/)![1];
    return [locale, v];
  })
) as Record<string, any>;

function siphonI18n(prefix = 'zh') {
  // 尝试加载src/locales下的文件
  const srcLocales = Object.fromEntries(
    Object.entries(import.meta.glob('../locales/*.json', { eager: true })).map(
      ([key, value]: any) => {
        const matched = key.match(/([A-Za-z0-9-_]+)\./i)[1];
        return [matched, value.default];
      }
    )
  );

  return srcLocales[prefix];
}

/**
 * 国际化转换工具函数（自动读取根目录locales文件夹下文件进行国际化匹配）
 * @param message message
 * @returns 转化后的message
 */
export function transformI18n(message: any = '') {
  if (!message) {
    return '';
  }

  // 获取当前语言
  const currentLang = useSystemConfigStoreHook()?.currentLang || Lang.LAN_CN;

  // 处理对象类型的标题 {zh: "首页", en: "Home"}
  if (typeof message === 'object' && message !== null) {
    // 确保返回当前语言的标题
    const title = message[currentLang];
    if (title) {
      return title;
    }
    // 如果当前语言没有对应的标题，尝试使用其他语言
    return message[Lang.LAN_CN] || message[Lang.LAN_EN] || Object.values(message)[0] || '';
  }

  // 处理字符串类型的标题
  if (typeof message === 'string') {
    // 尝试从 i18n 配置中获取翻译
    const key = message.match(/(\S*)\./)?.[1];
    const messages = siphonI18n(currentLang) || {};

    if (key && Object.keys(messages).includes(key)) {
      return (i18n.global as any).t(message as string);
    } else if (!key && Object.keys(messages).includes(message)) {
      return (i18n.global as any).t(message as string);
    }

    // 如果没有找到翻译，返回原字符串
    return message;
  }

  return '';
}

/* ---------- 默认语言：localStorage → zh ---------- */
const defaultLang = (storageLocal().getItem<string>(systemEnum.LOCALE_KEY) as Lang) ?? Lang.LAN_CN;

export const i18n = createI18n({
  legacy: false,
  locale: defaultLang,
  fallbackLocale: Lang.LAN_CN,
  messages,
});
